import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { GoogleMap ,MapInfoWindow, MapMarker } from '@angular/google-maps';
import { isArray, isEqual } from 'lodash';
import SystemConstants from 'src/app/util/constants/SystemConstants';
import { Translations } from 'src/app/util/service/internationalization/translations.service';
import { MapMode } from '../../enum/map-mode';
import { MapType } from '../../enum/map-type';
import { MapMarkerInfo } from '../../models/map-marker-info';

@Component({
    selector: 'app-custom-route-map-preview',
    templateUrl: './custom-route-map-preview.component.html',
    styleUrls: ['./custom-route-map-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CustomRouteMapPreviewComponent implements OnInit, OnChanges {

  @Input() destination: google.maps.LatLngLiteral;
  @Input() areMapTypeControlsHidden: boolean;
  @Input() mapType: MapType;
  @Input() mode: MapMode
  @Input() initialMarkersValue: MapMarkerInfo[]
  @Input() origin: google.maps.LatLngLiteral
  @Input() panelClass: string
  @Input() strokeWeight = SystemConstants.DEFAULT_STROKE_WEIGHT
  @Input() strokeColor = SystemConstants.DEFAULT_STROKE_COLOR
  @Input() token: string;
  @Input() zoom: number

  @Output() allMarkersRemoved: EventEmitter<string> = new EventEmitter()
  @Output() directionStatusChanged: EventEmitter<google.maps.DirectionsStatus> = new EventEmitter()
  @Output() markersUpdated: EventEmitter<MapMarkerInfo[]> = new EventEmitter<MapMarkerInfo[]>()
  @Output() noRouteFound: EventEmitter<string> = new EventEmitter<string>()
  @ViewChildren(MapInfoWindow)
  infoWindows!: QueryList<MapInfoWindow>;
  private currentInfoWindow: MapInfoWindow | null = null;

  isMarkerInfoVisible: boolean;
  isMarkerCreationInvalid = false
  markerInfoEditIndex = -1;
  markers: MapMarkerInfo[];
  directionsRenderer: google.maps.DirectionsRenderer;
  directionsService: google.maps.DirectionsService;
  mapInstance: google.maps.Map;
  
  // Cached options to avoid recalculation on every change detection
  private _mapOptions: google.maps.MapOptions;
  private _polylinePath: google.maps.LatLngLiteral[] = [];
  private _polylineOptions: google.maps.PolylineOptions;
  private _markerOptionsCache: Map<MapMarkerInfo, google.maps.MarkerOptions> = new Map();
  private directionsLoadTimeout: any;

  @ViewChild('map', { static: false }) googleMap: GoogleMap;

  constructor(
    public translations: Translations,
    private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.initializeDefaults();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setOperationModeChanges(changes)
    this.setViewModeChanges(changes)
    
    // Invalidate caches when relevant inputs change
    if (changes.origin || changes.destination || changes.zoom || changes.areMapTypeControlsHidden) {
      this._mapOptions = null;
    }
    if (changes.markers || changes.strokeWeight || changes.strokeColor) {
      this._polylinePath = null;
      this._polylineOptions = null;
      this._markerOptionsCache.clear();
    }
    
    if (this.isAutoMapSelected && (changes.origin || changes.destination)) {
      // Debounce directions loading
      if (this.directionsLoadTimeout) {
        clearTimeout(this.directionsLoadTimeout);
      }
      this.directionsLoadTimeout = setTimeout(() => {
        this.loadDirections();
      }, 300);
    }
    
    this.cdr.markForCheck();
  }

  closeMarkerInfoForm(): void {
    this.currentInfoWindow?.close();
    this.currentInfoWindow = null;
    this.markerInfoEditIndex = -1;
    this.isMarkerInfoVisible = false;
    this.cdr.markForCheck();
  }

  createRouterMark(event: google.maps.MapMouseEvent): void {
    if(!this.isOperationModeEnabled) {
      return
    }
    if(this.isManualMapSelected && this.markers && this.markers.length) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      if(!this.markers || !this.markers.length)
        this.markers = []
      this.markers.push({ latitude: lat, longitude: lng, mapInfo: '', mapLink: '', infoImage: '' });
      this.emitUpdatedMarkers()
    } else if (this.isManualMapSelected && (!this.markers || !this.markers.length)) {
      this.isMarkerCreationInvalid = true;
      this.cdr.markForCheck();
    }
  }

  doInitialMarkersExist(): boolean {
    return this.initialMarkersValue && isArray(this.initialMarkersValue) && this.initialMarkersValue.length > 0
  }

  emitUpdatedMarkers(): void {    
    if(this.isOperationModeEnabled) {
      // Invalidate caches when markers change
      this._polylinePath = null;
      this._polylineOptions = null;
      this._markerOptionsCache.clear();
      this.markersUpdated.emit(this.markers);
      this.cdr.markForCheck();
    }
  }

  initializeClassProperties(): void {    
    if(!this.origin) {
      this.origin = { lat: SystemConstants.DEFAULT_LATITUDE, lng: SystemConstants.DEFAULT_LONGITUDE}
    }
    if(this.doInitialMarkersExist()) {
      this.markers = Object.assign([], this.initialMarkersValue);
      this.emitUpdatedMarkers()
      this.initialMarkersValue = null
    }
    else
      this.markers = []
  }

  initializeDefaults(): void {
    this.loadMapWithDefaults();
    this.initializeClassProperties()
  }  

  isMapTypeUnchanged(changes: SimpleChanges): boolean {
    return !changes.mapType || (changes.mapType && changes.mapType.currentValue === changes.mapType.previousValue)
  }
  
  isMapTypesFirstChange(changes: SimpleChanges): boolean {
    return changes.mapType && changes.mapType.firstChange
  }

  isMarkersArrayUnchanged(): boolean {
    return isEqual(this.initialMarkersValue, this.markers)
  }

  loadMapWithDefaults(): void {    
    if(!this.mode)
      this.mode = MapMode.OPERATION
    
    // Google Maps is already loaded via script tag in index.html, so we can use it directly
    if (typeof google !== 'undefined' && google.maps) {
      if(!this.origin)
        this.setCurrentLocation();
      this.zoom = SystemConstants.MAX_ZOOM_IN_MAP;
      this.initializeDirectionsService();
      this.cdr.markForCheck();
    } else {
      // Fallback: wait for Google Maps to load (shouldn't be needed if script is in index.html)
      const checkInterval = setInterval(() => {
        if (typeof google !== 'undefined' && google.maps) {
          clearInterval(checkInterval);
          if(!this.origin)
            this.setCurrentLocation();
          this.zoom = SystemConstants.MAX_ZOOM_IN_MAP;
          this.initializeDirectionsService();
          this.cdr.markForCheck();
        }
      }, 100);
      setTimeout(() => {
        clearInterval(checkInterval);
      }, 5000);
    }
  }

  initializeDirectionsService(): void {
    if (typeof google !== 'undefined' && google.maps) {
      this.directionsService = new google.maps.DirectionsService();
      this.directionsRenderer = new google.maps.DirectionsRenderer({
        suppressMarkers: false
      });
    }
  }

  onMapInitialized(map: google.maps.Map): void {
    this.mapInstance = map;
    if (this.isAutoMapSelected && this.origin && this.destination) {
      // Small delay to ensure map is fully ready
      setTimeout(() => {
        this.loadDirections();
      }, 100);
    }
    this.cdr.markForCheck();
  }

  loadDirections(): void {
    if (!this.isAutoMapSelected || !this.origin || !this.destination) {
      if (this.directionsRenderer) {
        this.directionsRenderer.setMap(null);
      }
      return;
    }

    if (!this.directionsService || !this.directionsRenderer) {
      this.initializeDirectionsService();
    }

    if (!this.mapInstance) {
      return;
    }

    if (this.directionsService && this.directionsRenderer && this.mapInstance) {
      this.directionsRenderer.setMap(this.mapInstance);

      const request: google.maps.DirectionsRequest = {
        origin: this.origin,
        destination: this.destination,
        travelMode: google.maps.TravelMode.DRIVING
      };

      this.directionsService.route(request, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          this.directionsRenderer.setDirections(result);
          this.showDirectionStatus(google.maps.DirectionsStatus.OK);
        } else {
          this.showDirectionStatus(status as google.maps.DirectionsStatus);
        }
        this.cdr.markForCheck();
      });
    }
  }

openMapInfo(marker: MapMarker, index: number): void {
  if (!this.isManualMapSelected || !this.isOperationModeEnabled) {
    return;
  }
  if (this.currentInfoWindow) {
    this.currentInfoWindow.close();
  }
  this.markerInfoEditIndex = index;
  this.isMarkerInfoVisible = true;

  this.cdr.detectChanges();

  const infoWindow = this.infoWindows.toArray()[index];
  if (infoWindow) {
    this.currentInfoWindow = infoWindow;  
    infoWindow.open(marker);
  }
}

  removeMarker(): void {
    if(this.isOperationModeEnabled) {
      if(this.markerInfoEditIndex > -1 && this.markers && this.markers[this.markerInfoEditIndex])
        this.markers.splice(this.markerInfoEditIndex, 1)
      if(this.markers && this.markers.length === 0)
        this.allMarkersRemoved.emit();
      this.closeMarkerInfoForm()
      this.emitUpdatedMarkers()
    }
  }

  setDefaultOriginAndZoom(): void {    
    this.origin = {
      lat: SystemConstants.DEFAULT_LATITUDE,
      lng: SystemConstants.DEFAULT_LONGITUDE
    }
    this.zoom = SystemConstants.MAX_ZOOM_IN_MAP;
    this._mapOptions = null; // Invalidate cache
    this.cdr.markForCheck();
  }

  saveMarkerInfo(markerInfo: MapMarkerInfo): void{
    if(this.markers && this.markers[this.markerInfoEditIndex])
      this.markers[this.markerInfoEditIndex] = markerInfo
    this.closeMarkerInfoForm();
    this.emitUpdatedMarkers()
  }
  
  setMarkerForManualMap(changes?: SimpleChanges): void {
    if(this.origin && ( !this.markers || !this.markers.length
        || (changes && changes.origin && changes.origin.currentValue!==changes.origin.previousValue)
        ) && (!this.doInitialMarkersExist() || !this.isMarkersArrayUnchanged())  ) {
      let originMarker = new MapMarkerInfo({latitude: this.origin.lat, longitude: this.origin.lng});
      this.markers = [originMarker]
      this.isMarkerCreationInvalid = false
      this.emitUpdatedMarkers()
    }
  }

  private setCurrentLocation(): void {
    try {
      if (SystemConstants.GEOLOCATION in navigator) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.origin = {
            lat:  position && position.coords && position.coords.latitude || SystemConstants.DEFAULT_LATITUDE,
            lng: position && position.coords && position.coords.longitude || SystemConstants.DEFAULT_LONGITUDE
          }
          this.zoom = SystemConstants.MAX_ZOOM_IN_MAP;
          this._mapOptions = null; // Invalidate cache
          this.cdr.markForCheck();
        });
      } else {
        this.setDefaultOriginAndZoom()
      }

      } catch(error) {
        this.setDefaultOriginAndZoom()
      }
  }

  setOperationModeChanges(changes: SimpleChanges): void {
    if(this.isOperationModeEnabled) {
      if(!this.isMapTypeUnchanged(changes) || this.isMapTypesFirstChange(changes)) {
        this.markers = []
        this.isMarkerInfoVisible = false
        this.markerInfoEditIndex = -1
        this.isMarkerCreationInvalid = false;
      }
      if((this.isMapTypeUnchanged(changes) || this.isMapTypesFirstChange(changes))) {
        this.setMarkerForManualMap(changes)
      }
    }
  }

  setViewModeChanges(changes: SimpleChanges): void {
    if(this.isViewModeEnabled) {
      if(changes.initialMarkersValue) {
        this.markers = [...this.initialMarkersValue]
      }
    }}

  showDirectionStatus(directionStatus: google.maps.DirectionsStatus): void {
    this.directionStatusChanged.emit(directionStatus);
    if(directionStatus === google.maps.DirectionsStatus.ZERO_RESULTS)
      this.noRouteFound.emit()
  }

  get isAutoMapSelected(): boolean {
    return this.mapType === MapType.AUTO
  }

  get isManualMapSelected(): boolean {
    return this.mapType === MapType.MANUAL
  }

  get isOperationModeEnabled(): boolean {
    return this.mode === MapMode.OPERATION
  }

  get isViewModeEnabled(): boolean {
    return this.mode === MapMode.VIEW
  }

  get polylinePath(): google.maps.LatLngLiteral[] {
    if (this._polylinePath && this._polylinePath.length > 0) {
      return this._polylinePath;
    }
    if (!this.markers || this.markers.length === 0) {
      this._polylinePath = [];
      return [];
    }
    this._polylinePath = this.markers.map(m => ({ lat: m.latitude, lng: m.longitude }));
    return this._polylinePath;
  }

  get polylineOptions(): google.maps.PolylineOptions {
    if (this._polylineOptions) {
      return this._polylineOptions;
    }
    this._polylineOptions = {
      path: this.polylinePath,
      strokeWeight: this.strokeWeight,
      strokeColor: this.strokeColor,
      visible: true
    };
    return this._polylineOptions;
  }

  getMarkerOptions(marker: MapMarkerInfo): google.maps.MarkerOptions {
    if (this._markerOptionsCache.has(marker)) {
      return this._markerOptionsCache.get(marker);
    }
    const options: google.maps.MarkerOptions = {
      position: { lat: marker.latitude, lng: marker.longitude },
      label: marker.mapInfo ? { text: marker.mapInfo, color: '#000', fontSize: '16px' } : null
    };
    this._markerOptionsCache.set(marker, options);
    return options;
  }

  getMapOptions(): google.maps.MapOptions {
    if (this._mapOptions) {
      return this._mapOptions;
    }
    this._mapOptions = {
      mapTypeControl: this.areMapTypeControlsHidden ? false : true,
      center: this.origin ? { lat: this.origin.lat, lng: this.origin.lng } : null,
      zoom: this.zoom,
      scrollwheel: null,
      gestureHandling: 'cooperative'
    };
    return this._mapOptions;
  }

}
